/**
 * Context-sensitive help system.
 * Maps tools, panels, and dialogs to help page URLs.
 * Handles F1 key globally and provides openHelp() for programmatic use.
 */
export class HelpSystem {
  constructor(state) {
    this.state = state;
    this._basePath = 'help/';

    /** Tool/context -> help page URL fragment */
    this._topicMap = {
      // Tools
      pencil: 'tools.html#pencil',
      rect: 'tools.html#rectangle',
      fill: 'tools.html#fill',
      eraser: 'tools.html#eraser',
      select: 'tools.html#select',
      property: 'tools.html#property-brush',
      object: 'tools.html#object',
      terrain: 'tools.html#terrain-brush',

      // Panels
      layers: 'layers.html',
      palette: 'tilesets.html',

      // Generators
      generators: 'generators.html',

      // File ops
      'file-open': 'file-formats.html#opening-files',
      'file-save': 'file-formats.html#saving-files',
      'export-png': 'file-formats.html#export-png',
      'export-tmx': 'file-formats.html#tmx-export',

      // Integrations
      'engine-export': 'engine-export.html',
      github: 'github-integration.html',
      cloud: 'cloud-storage.html',
      auth: 'cloud-storage.html#echoforge-account',

      // Reference
      shortcuts: 'keyboard-shortcuts.html',

      // Tile editors
      'tile-animation': 'tile-animations.html',
      'tile-collision': 'tile-collisions.html',

      // PixelLab
      pixellab: 'pixellab.html',

      // Tutorial (handled specially)
      tutorial: null,
    };

    this._tutorial = null;
    this._activeContext = null;

    document.addEventListener('keydown', (e) => this._onKeyDown(e));
  }

  /**
   * Register the tutorial instance so openTopic('tutorial') can start it.
   */
  setTutorial(tutorial) {
    this._tutorial = tutorial;
  }

  /**
   * Set the active help context (overrides tool-based detection).
   * Call with null to clear the override.
   */
  setContext(context) {
    this._activeContext = context;
  }

  /**
   * Open help for a specific topic.
   * @param {string} topic - Key from the topic map
   */
  openTopic(topic) {
    if (topic === 'tutorial' && this._tutorial) {
      this._tutorial.start();
      return;
    }
    const page = this._topicMap[topic];
    if (page) {
      window.open(this._basePath + page, '_blank');
    } else {
      window.open(this._basePath + 'index.html', '_blank');
    }
  }

  /**
   * Open help for the current context (active tool or override).
   */
  openContextHelp() {
    if (this._activeContext) {
      this.openTopic(this._activeContext);
      return;
    }
    // Fall back to active tool
    const activeTool = this.state.get('activeTool');
    if (activeTool && this._topicMap[activeTool]) {
      this.openTopic(activeTool);
    } else {
      this.openTopic('index');
    }
  }

  /**
   * Get the help URL for a topic (without opening it).
   */
  getTopicUrl(topic) {
    const page = this._topicMap[topic] || 'index.html';
    return this._basePath + page;
  }

  _onKeyDown(e) {
    if (e.key === 'F1') {
      e.preventDefault();
      this.openContextHelp();
    }
  }
}
